import React, { useCallback } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import ThirdPane from '../ThirdPane';
import CustomerDetail from './CustomerDetail';
import { Customer } from './types';
import { PortalWithState } from 'react-portal';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader from '../ThirdPane/ThirdPaneHeader';
import Button from '../Button/Button';
import CustomerEditModal from './CustomerEditModal';
import useMode from '../../hooks/useMode';
import { UserMode } from '../../hooks/useApolloClient';
import useNotify from '../../hooks/useNotify';
import { ApolloError } from '@apollo/client/errors';

type CustomerThirdPaneProps = {
  customerId?: string;
  prevEnabled: boolean;
  nextEnabled: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onToggle?: (visible: boolean) => void;
};

const SET_CUSTOMER_STATUS = gql`
  mutation SetCustomerStatus($customerId: ID!, $status: ContractStatus!) {
    setCustomerStatus(customerId: $customerId, status: $status) {
      id
      status
    }
  }
`;

export const CUSTOMER_FIELDS = (mode: UserMode) => `
  id
  profileId
  accountNumber
  status
  serviceAddresses (
  ${mode === 'water-supplier' ? 'where: { waterServiceId: { ne: null } }' : ''}
    limit: null
  ) {
    id
    active
    address {
      id
      street1
      street2
      city
      state
      zip
      zipExt
    }
    ${
      mode === 'water-supplier'
        ? `waterService {
      id
      siteNumber
      status
    }`
        : ''
    }
  }
  textEnabled
  emailEnabled
  mailEnabled
  profile {
    id
    type
    name
    firstName
    lastName
    contactAddressId
    contactAddress {
      id
      street1
      street2
      city
      state
      zip
      zipExt
    }
    billingAddressId
    billingAddress {
      id
      street1
      street2
      city
      state
      zip
      zipExt
    }
    phones {
      id
      subType
      value
      extension
    }
    emails {
      id
      subType
      value
    }
    perms
  }
  notes
`;

export default function CustomerThirdPane({
  customerId,
  onPrev,
  onNext,
  onToggle,
  prevEnabled,
  nextEnabled,
}: CustomerThirdPaneProps): JSX.Element {
  const mode = useMode();

  const GET_CUSTOMER_DETAIL = gql`
    query GetCustomerDetail($where: JSON) {
      customers(where: $where) {
        ${CUSTOMER_FIELDS(mode)}
      }
    }
  `;

  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
  } = useQuery<{
    customers: Customer[];
  }>(GET_CUSTOMER_DETAIL, {
    skip: !customerId,
    variables: { where: { id: customerId || null } },
  });

  const { showError } = useNotify();

  const customer = itemData?.customers[0];

  const [
    setCustomerStatus,
    { data, loading: mutationLoading, error: mutationError },
  ] = useMutation(SET_CUSTOMER_STATUS, {
    onError: useCallback((error: ApolloError) => {
      showError('Error setting customer status', error);
    }, []),
  });

  const activateDeactivate = () => {
    if (!customer) {
      return;
    }
    customer.status === 'ACTIVE'
      ? setCustomerStatus({
          variables: { customerId: customer.id, status: 'INACTIVE' },
        })
      : setCustomerStatus({
          variables: { customerId: customer.id, status: 'ACTIVE' },
        });
  };

  return (
    <>
      <ThirdPane visible={!!customerId}>
        <ThirdPaneHeader
          onToggle={onToggle}
          title={customer ? customer.profile.name : 'Loading...'}
          onPrev={onPrev}
          onNext={onNext}
          prevEnabled={prevEnabled}
          nextEnabled={nextEnabled}
        >
          <Button
            mr={3}
            variant={customer?.status === 'ACTIVE' ? 'danger' : 'outline'}
            onClick={activateDeactivate}
          >
            {customer?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
          </Button>
          {customer && (
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button
                    mr={10}
                    icon="edit"
                    variant="outline"
                    onClick={openPortal}
                  >
                    Edit
                  </Button>
                  {portal(
                    <CustomerEditModal
                      action="edit"
                      perms={customer.profile.perms}
                      onClose={closePortal}
                      customer={customer}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          )}
        </ThirdPaneHeader>
        <ThirdPaneContent>
          {customer && <CustomerDetail customerDetail={customer} />}
        </ThirdPaneContent>
      </ThirdPane>
    </>
  );
}
